import {
  type AdminUserListItem,
  formatSandboxRuntime,
  formatSandboxTransfer,
  type PresenceStatus,
  SANDBOX_USAGE_WARNING_PERCENT,
  sandboxUsageRatio,
} from '@kyumo/common';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  CircleHelp,
  Loader2,
  Search,
  ShieldCheck,
  ShieldOff,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentMonth } from '../../components/admin/chart-utils';
import { useWebAuth } from '../../contexts/WebAuthContext';
import { useAccountUsageData, useCostLimits, useUsers } from '../../hooks/use-admin-api';
import { usePresence } from '../../hooks/use-presence';

/**
 * ユーザー一覧ページ
 */
const LIMIT_OPTIONS = [10, 25, 50] as const;

type SortKey =
  | 'email'
  | 'presence'
  | 'status'
  | 'enabled'
  | 'mfa'
  | 'cost'
  | 'limit'
  | 'sandbox'
  | 'createdAt'
  | 'lastLogin';

type SortDirection = 'asc' | 'desc';

/**
 * サンドボックスの上限到達度（%）。
 *
 * 稼働時間と転送量のうち**厳しいほう**を採る。どちらか一方でも達すれば新しい
 * 実行環境を確保できなくなるため、平均や片側だけでは止まっている人を見落とす。
 */
function sandboxPeakPercent(usage: AdminUserListItem['sandboxUsage']): number {
  if (!usage) return 0;
  // 丸めない共有の到達度を使う（`sandboxUsageRatio`）。ここだけ別実装にすると、
  // 同じ利用者について画面ごとに違う段階を示すことになる
  return Math.max(
    sandboxUsageRatio(usage.runtimeSeconds, usage.limits.runtimeSeconds),
    sandboxUsageRatio(usage.transferBytes, usage.limits.transferBytes),
  );
}

/**
 * 表示上の到達状態。
 *
 * **`unknown`（取得失敗）と `pending`（未取得）を `offline` と分けて持つ。**
 * 一緒くたにすると、到達性の取得に失敗しているだけの状態が「相手がいない」に
 * 化ける。依頼先の判断に使う値である以上、この 2 つを同じ見た目にはできない。
 */
type PresenceDisplayStatus = PresenceStatus | 'pending' | 'unknown';

/** 並べ替え用の重み。オンラインをまとめて確認できるよう、オンラインを先頭に寄せる */
const PRESENCE_SORT_WEIGHT: Record<PresenceDisplayStatus, number> = {
  online: 0,
  offline: 1,
  unknown: 2,
  pending: 3,
};

export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [limit, setLimit] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { data: usersData, isLoading, error } = useUsers();
  const { data: accountUsageData } = useAccountUsageData(getCurrentMonth());
  const { data: costLimitsData } = useCostLimits();
  const { user: currentUser } = useWebAuth();
  // 稼働状態の参照は監視にあたるため、auditグループ所属時のみ取得・表示する
  const hasAuditGroup = currentUser?.groups?.includes('audit') ?? false;
  // 到達性はダッシュボードと同じクエリ鍵を共有する（同じ画面の 2 箇所が
  // 別の時点の判定を見ないようにするため）
  const presence = usePresence({ enabled: hasAuditGroup });

  /**
   * 行ごとの到達状態を決める。
   *
   * **写像に無い利用者はオフライン。** 応答には到達性の記録がある利用者しか
   * 含まれないため、含まれないことがそのままオフラインを意味する。
   * ただし取得できていない・失敗しているあいだは、その解釈を当てはめない。
   */
  const presenceStatusOf = (email: string): PresenceDisplayStatus => {
    if (presence.isError) return 'unknown';
    if (presence.isLoading) return 'pending';
    return presence.statusByEmail.get(email) ?? 'offline';
  };

  // ユーザー別コストのルックアップ（アカウント別の全件から取得）。
  // ダッシュボードのランキングは上位 10 件に切られており、それ以外の利用者が
  // 「-」になって詳細画面の利用料金と食い違うため使わない
  const costByEmail = useMemo(() => {
    const map = new Map<string, number>();
    if (accountUsageData?.users) {
      for (const item of accountUsageData.users) {
        map.set(item.email, item.cost);
      }
    }
    return map;
  }, [accountUsageData?.users]);

  // デフォルト上限
  const defaultLimitUSD = costLimitsData?.defaultLimit?.limitUSD ?? null;

  // ユーザー別個別上限のルックアップ
  const userLimitByEmail = useMemo(() => {
    const map = new Map<string, number>();
    if (costLimitsData?.userLimits) {
      for (const entry of costLimitsData.userLimits) {
        map.set(entry.email, entry.setting.limitUSD);
      }
    }
    return map;
  }, [costLimitsData?.userLimits]);

  // 検索クエリのデバウンス処理（ページもリセット）
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [searchQuery]);

  // フィルタリング（部分一致）
  const filteredUsers = useMemo(() => {
    if (!usersData?.users) return [];
    if (!debouncedSearch) return usersData.users;
    const query = debouncedSearch.toLowerCase();
    return usersData.users.filter((user: { email: string }) =>
      user.email.toLowerCase().includes(query),
    );
  }, [usersData?.users, debouncedSearch]);

  // ソート（未指定時は元の順序を維持）
  // biome-ignore lint/correctness/useExhaustiveDependencies: presenceStatusOf は presence から導かれるため、presence を依存に置けば足りる
  const sortedUsers = useMemo(() => {
    if (!sortKey) return filteredUsers;
    const dir = sortDirection === 'asc' ? 1 : -1;
    // ソート用の値を取り出す（欠損値は数値 0 として扱う）
    const getSortValue = (user: AdminUserListItem): string | number => {
      switch (sortKey) {
        case 'email':
          return user.email;
        case 'presence':
          return PRESENCE_SORT_WEIGHT[presenceStatusOf(user.email)];
        case 'status':
          return user.status;
        case 'enabled':
          return user.enabled ? 1 : 0;
        case 'mfa':
          // 取得できなかった利用者は未登録よりさらに後ろ（昇順で先頭）に寄せる
          return user.mfaEnabled === undefined ? -1 : user.mfaEnabled ? 1 : 0;
        case 'cost':
          return costByEmail.get(user.email) ?? 0;
        case 'limit':
          return userLimitByEmail.get(user.email) ?? defaultLimitUSD ?? 0;
        case 'sandbox':
          return sandboxPeakPercent(user.sandboxUsage);
        case 'createdAt':
          return user.createdAt ?? 0;
        case 'lastLogin':
          return user.lastLogin ?? 0;
      }
    };
    return [...filteredUsers].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (typeof va === 'string' && typeof vb === 'string') {
        return va.localeCompare(vb, 'ja') * dir;
      }
      return ((va as number) - (vb as number)) * dir;
    });
  }, [
    filteredUsers,
    sortKey,
    sortDirection,
    costByEmail,
    userLimitByEmail,
    defaultLimitUSD,
    presence,
  ]);

  // ヘッダークリックでソート切り替え（昇順 → 降順 → 解除）
  const handleSort = (key: SortKey) => {
    setCurrentPage(0);
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortKey(null);
      setSortDirection('asc');
    }
  };

  // ページネーション
  const totalPages = Math.ceil(sortedUsers.length / limit);
  const currentUsers = useMemo(() => {
    const start = currentPage * limit;
    return sortedUsers.slice(start, start + limit);
  }, [sortedUsers, currentPage, limit]);

  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const handlePreviousPage = () => {
    if (canGoPrevious) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNextPage = () => {
    if (canGoNext) {
      setCurrentPage((p) => p + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">データの取得に失敗しました</p>
          <p className="text-muted-foreground text-sm mt-2">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* ヘッダー */}
      <div
        className="flex flex-col gap-4 mb-8 md:flex-row md:items-center md:justify-between"
        data-testid="page-header"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground">ユーザー一覧</h1>
          <p className="text-muted-foreground mt-1">
            {filteredUsers.length > 0
              ? `${currentPage * limit + 1} - ${currentPage * limit + currentUsers.length} / ${filteredUsers.length} 件`
              : '0 件'}
          </p>
        </div>

        {/* 検索 */}
        <div className="relative w-full md:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="メールアドレスで検索..."
            className="w-full md:w-64 pl-10 pr-10 py-2 bg-card border border-border rounded-lg text-foreground placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ユーザーテーブル */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[72rem]">
            <thead>
              <tr className="text-left text-muted-foreground text-sm bg-muted/50">
                <SortableHeader
                  label="メールアドレス"
                  sortKey="email"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                {hasAuditGroup && (
                  <SortableHeader
                    label="到達状態"
                    sortKey="presence"
                    activeSortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                )}
                <SortableHeader
                  label="ステータス"
                  sortKey="status"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="有効"
                  sortKey="enabled"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="MFA"
                  sortKey="mfa"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="当月使用量"
                  sortKey="cost"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  label="実効上限"
                  sortKey="limit"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  label="サンドボックス"
                  sortKey="sandbox"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  label="作成日"
                  sortKey="createdAt"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="最終更新"
                  sortKey="lastLogin"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {currentUsers.length > 0 ? (
                currentUsers.map((user: AdminUserListItem) => (
                  <tr key={user.email} className="hover:bg-muted/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/users/${encodeURIComponent(user.email)}`}
                        className="text-foreground hover:text-primary-400 transition-colors font-medium"
                      >
                        {user.email}
                      </Link>
                    </td>
                    {hasAuditGroup && (
                      <td className="px-6 py-4">
                        <PresenceCell
                          email={user.email}
                          status={presenceStatusOf(user.email)}
                          lastSeenAt={presence.lastSeenByEmail.get(user.email)}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-6 py-4">
                      {user.enabled ? (
                        <span className="flex items-center gap-1 text-green-400">
                          <UserCheck className="w-4 h-4" />
                          有効
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400">
                          <UserX className="w-4 h-4" />
                          無効
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <MfaCell enabled={user.mfaEnabled} />
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums">
                      <CostCell
                        cost={costByEmail.get(user.email)}
                        limit={userLimitByEmail.get(user.email) ?? defaultLimitUSD}
                      />
                    </td>
                    <td className="px-6 py-4 text-right text-muted-foreground tabular-nums">
                      {(() => {
                        const limit = userLimitByEmail.get(user.email) ?? defaultLimitUSD;
                        return limit !== null ? `$${limit.toFixed(2)}` : '-';
                      })()}
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums">
                      <SandboxUsageCell email={user.email} usage={user.sandboxUsage} />
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ja-JP') : '-'}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('ja-JP') : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={hasAuditGroup ? 10 : 9}
                    className="px-6 py-12 text-center text-muted-foreground"
                  >
                    ユーザーが見つかりません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/*
        到達状態の読み方の補足。
        **オフラインは「使っていない」ことの証明ではない。** 判定はアプリからの
        定期的な申告に基づくため、申告を送らない旧バージョンや、アプリを閉じている
        だけの利用者もオフラインとして並ぶ
      */}
      {hasAuditGroup && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="presence-note">
          到達状態はデスクトップアプリからの定期的な申告に基づきます。オフラインの表示は、アプリを利用していないことの証明ではありません（アプリを閉じている・スリープ中・申告に対応していない版を使っている場合もオフラインになります）。
        </p>
      )}

      {/* ページネーション */}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-4 md:gap-6">
        {/* 表示件数選択 */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">表示</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-2 py-1 bg-muted border border-border rounded text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {LIMIT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="text-muted-foreground">件</span>
        </div>

        {/* ページ送り */}
        <div className="flex items-center">
          <button
            type="button"
            onClick={handlePreviousPage}
            disabled={!canGoPrevious}
            className="p-3 md:p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="px-2 text-sm text-muted-foreground min-w-[60px] text-center">
            {totalPages > 0 ? `${currentPage + 1} / ${totalPages}` : '-'}
          </span>
          <button
            type="button"
            onClick={handleNextPage}
            disabled={!canGoNext}
            className="p-3 md:p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ソート可能なテーブルヘッダー
 * クリックで昇順 → 降順 → 解除を切り替える
 */
function SortableHeader({
  label,
  sortKey,
  activeSortKey,
  sortDirection,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey | null;
  sortDirection: SortDirection;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const isActive = activeSortKey === sortKey;
  return (
    <th className={`px-6 py-4 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`flex w-full items-center gap-1 font-medium transition-colors hover:text-foreground ${
          align === 'right' ? 'justify-end' : ''
        } ${isActive ? 'text-foreground' : ''}`}
      >
        {label}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )
        ) : (
          <ChevronsUpDown className="w-4 h-4 opacity-40" />
        )}
      </button>
    </th>
  );
}

/**
 * コスト表示セル
 * 上限に対する使用率で色を変化させる
 */
function CostCell({ cost, limit }: { cost: number | undefined; limit: number | null }) {
  if (cost === undefined || cost === 0) {
    return <span className="text-muted-foreground">-</span>;
  }
  const percent = limit !== null && limit > 0 ? (cost / limit) * 100 : 0;
  const color =
    percent >= 100 ? 'text-red-400' : percent >= 80 ? 'text-yellow-400' : 'text-foreground';
  return <span className={color}>${cost.toFixed(2)}</span>;
}

/**
 * サンドボックス使用量セル
 *
 * **上限に達している利用者を一覧で見つけられるようにする。** 到達度を色で分け、
 * 実数も並べる（到達度だけでは、稼働時間と転送量のどちらで詰まっているかが
 * 分からない）。コスト上限と同じ段階（80%）で色を変える。
 *
 * クラウド実行先を使っていない利用者は計上そのものが無い。0% を並べると、
 * 本当に見るべき行が埋もれる。
 */
function SandboxUsageCell({
  email,
  usage,
}: {
  email: string;
  usage: AdminUserListItem['sandboxUsage'];
}) {
  if (!usage) {
    return <span className="text-muted-foreground">-</span>;
  }
  const percent = sandboxPeakPercent(usage);
  const color =
    percent >= 100
      ? 'text-red-400'
      : percent >= SANDBOX_USAGE_WARNING_PERCENT
        ? 'text-yellow-400'
        : 'text-foreground';

  return (
    // **行を特定できる印にする。** 集計月は一覧の全行で同じ値になるため、
    // 月で名付けると同じ印が行の数だけ並び、特定の利用者のセルを指せない
    <span
      className="flex flex-col items-end"
      data-testid={`sandbox-usage-${email}`}
      data-month={usage.month}
    >
      {/*
        表示は切り捨てる。四捨五入だと、達していないのに 100% と掲げる
        （カード表示（`SandboxUsageCard`）と同じ導き方に揃える）
      */}
      <span className={color}>{Math.floor(percent)}%</span>
      <span className="text-xs text-muted-foreground">
        {formatSandboxRuntime(usage.runtimeSeconds)} / {formatSandboxTransfer(usage.transferBytes)}
      </span>
    </span>
  );
}

/**
 * 到達状態セル
 *
 * **未取得・不明をオフラインと同じ見た目にしない。** 取得できていないことと
 * 「相手がいない」ことは別であり、同じに見せると依頼先の判断を誤らせる。
 *
 * 最終確認時刻は `data-last-seen-at` に生の値を残す。表示は相対的に読める
 * 時刻にするが、**「アプリを起動した時刻」ではない**ため、起動時刻と
 * 読めるような見出しは付けない。
 */
function PresenceCell({
  email,
  status,
  lastSeenAt,
}: {
  email: string;
  status: PresenceDisplayStatus;
  lastSeenAt: number | undefined;
}) {
  const config: Record<
    PresenceDisplayStatus,
    { label: string; className: string; dotClassName?: string }
  > = {
    online: {
      label: 'オンライン',
      className: 'text-green-400',
      dotClassName: 'fill-green-400 text-green-400',
    },
    offline: {
      label: 'オフライン',
      className: 'text-muted-foreground',
      dotClassName: 'text-muted-foreground',
    },
    unknown: { label: '不明', className: 'text-yellow-400' },
    pending: { label: '未取得', className: 'text-muted-foreground' },
  };
  const { label, className, dotClassName } = config[status];

  return (
    <span
      className="flex flex-col"
      data-testid={`presence-${email}`}
      data-presence-status={status}
      {...(lastSeenAt !== undefined ? { 'data-last-seen-at': String(lastSeenAt) } : {})}
    >
      <span className={`flex items-center gap-1 ${className}`}>
        {dotClassName ? (
          <Circle className={`w-2.5 h-2.5 ${dotClassName}`} />
        ) : (
          <CircleHelp className="w-3.5 h-3.5" />
        )}
        {label}
      </span>
      {status === 'online' && lastSeenAt !== undefined && (
        <span className="text-xs text-muted-foreground">
          最終確認 {new Date(lastSeenAt).toLocaleTimeString('ja-JP')}
        </span>
      )}
    </span>
  );
}

/**
 * ステータスバッジコンポーネント
 */
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; className: string }> = {
    CONFIRMED: { label: '確認済み', className: 'bg-green-500/20 text-green-400' },
    UNCONFIRMED: { label: '未確認', className: 'bg-yellow-500/20 text-yellow-400' },
    ARCHIVED: { label: 'アーカイブ', className: 'bg-gray-500/20 text-gray-400' },
    COMPROMISED: { label: '侵害', className: 'bg-red-500/20 text-red-400' },
    UNKNOWN: { label: '不明', className: 'bg-gray-500/20 text-gray-400' },
    RESET_REQUIRED: {
      label: 'リセット必要',
      className: 'bg-orange-500/20 text-orange-400',
    },
    FORCE_CHANGE_PASSWORD: {
      label: 'パスワード変更必要',
      className: 'bg-orange-500/20 text-orange-400',
    },
  };

  const config = statusConfig[status] || {
    label: status,
    className: 'bg-gray-500/20 text-gray-400',
  };

  return <span className={`px-2 py-1 text-xs rounded ${config.className}`}>{config.label}</span>;
}

/**
 * 認証アプリ（TOTP）の登録状況。
 *
 * 取得に失敗した利用者は「未登録」と見分けがつくよう、別の見た目（-）にする。
 */
function MfaCell({ enabled }: { enabled: boolean | undefined }) {
  if (enabled === undefined) {
    return (
      <span className="text-muted-foreground" title="登録状況を取得できませんでした">
        -
      </span>
    );
  }
  return enabled ? (
    <span className="flex items-center gap-1 text-green-400">
      <ShieldCheck className="w-4 h-4" />
      登録済み
    </span>
  ) : (
    <span className="flex items-center gap-1 text-muted-foreground">
      <ShieldOff className="w-4 h-4" />
      未登録
    </span>
  );
}
